import React, { useEffect, useState } from 'react';
import { Wallet, MapPin, Flag, PencilSimple, Archive, Plus, Article, Users, Trash, Link as LinkIcon, Eye, Check, HouseLine } from '@phosphor-icons/react';
import { ProfileStatusBar } from '../components/dashboard/ProfileStatusBar';
import { ProfileCompletenessNudge } from '../components/dashboard/ProfileCompletenessNudge';
import { FinalizedAnuntCard } from '../components/dashboard/FinalizedAnuntCard';
import { AnuntHistoryCard } from '../components/dashboard/AnuntHistoryCard';
import { AnuntSnapshotView } from '../components/dashboard/AnuntSnapshotView';
import { EmptyAnuntSlot } from '../components/dashboard/EmptyAnuntSlot';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ActionMenu } from '../components/ui/ActionMenu';
import { MovedInModal } from '../components/forms/MovedInModal';
import { RequestReviewModal } from '../components/forms/RequestReviewModal';
import { getSession, formatBudget, ProfileStatus, ComboMember, getAnuntFinalization, getOffersForTenant, markOfferAsSucces, finalizeAnuntExternally, cancelFinalization, updateSession, getAnuntHistory, archiveCurrentAnuntManually, removeAnuntHistory, AnuntHistoryEntry, isSuccesDismissed, dismissSucces, supplySlugFromEmail } from '../lib/utils';
import { HOUSING_TYPES } from '../lib/constants';
import { PAGE_ROUTE } from '../lib/routes';
interface AnuntData {
  id: string;
  slug: string;
  status: ProfileStatus;
  views: number;
  offers: number;
  budgetMaxEur: number;
  housingType: string;
  rooms: string;
  floor: string;
  yearBuiltLabel: string;
  neighborhoods: string;
  moveInDate: string;
  contractDuration: string;
  livingMode: string;
  isSmoker: boolean;
  pets: string | null;
  searchBio?: string;
  amenities: string[];
  // Group members (MVP, invite-only). The author is always the first
  // member. When members.length < 2, the listing is treated as solo and
  // the "Membri grup" section is hidden entirely.
  comboMembers?: ComboMember[];
  // Slug/id of the author (so we know which row to make non-removable).
  // Defaults to the first member when not provided.
  comboAuthorId?: string;
}
const ANDREI_ANUNT: AnuntData = {
  id: 'a1',
  slug: 'andrei-popescu',
  status: 'active',
  views: 142,
  offers: 3,
  budgetMaxEur: 550,
  housingType: 'apartament_bloc',
  rooms: '2 cam',
  floor: 'Etaj 2-5',
  yearBuiltLabel: '> 2000',
  neighborhoods: 'Cotroceni, Militari',
  moveInDate: 'Iunie, 2026',
  contractDuration: '2 ani',
  livingMode: 'Singur(ă)',
  isSmoker: false,
  pets: null,
  searchBio: 'Caut un apartament luminos, preferabil cu un spațiu care să poată fi amenajat ca birou. Aproape de metrou ar fi un mare plus.',
  amenities: ['Aer condiționat', 'Centrală termică', 'Balcon / Terasă', 'Mobilat complet']
};
const OLIVIA_ANUNT: AnuntData = {
  id: 'a2',
  slug: 'olivia-p',
  status: 'active',
  views: 287,
  offers: 5,
  budgetMaxEur: 650,
  housingType: 'apartament_bloc',
  rooms: '3 cam',
  floor: 'Etaj 1-3',
  yearBuiltLabel: '> 1990',
  neighborhoods: 'Floreasca, Aviatorilor',
  moveInDate: 'Octombrie, 2026',
  contractDuration: '1 an',
  livingMode: 'Cuplu',
  isSmoker: false,
  pets: '1 pisică',
  searchBio: 'Suntem un cuplu liniștit, lucrăm amândoi de acasă. Căutăm un apartament luminos cu balcon și spațiu pentru birou. Avem o pisică foarte educată.',
  amenities: ['Centrală termică', 'Parcare', 'Aer condiționat', 'Mobilat complet', 'Balcon / Terasă', 'Lift', 'Mașină de spălat vase']
};
const SARA_ANUNT: AnuntData = {
  id: 'a3',
  slug: 'combo-pair',
  status: 'active',
  views: 198,
  offers: 4,
  budgetMaxEur: 900,
  housingType: 'apartament_bloc',
  rooms: '3 cam',
  floor: 'Etaj 2-5',
  yearBuiltLabel: '> 2000',
  neighborhoods: 'Cotroceni, Centru',
  moveInDate: 'Iulie, 2026',
  contractDuration: '2 ani',
  livingMode: 'Co-living',
  isSmoker: false,
  pets: '1 pisică',
  searchBio: 'Suntem trei prieteni care căutăm un apartament de 3 camere în Cotroceni. Ne place liniștea, gătim acasă des și avem o pisică. Toți avem joburi stabile și plătim chiria împreună.',
  amenities: ['Parcare', 'Mobilat complet', 'Balcon / Terasă', 'Lift'],
  comboMembers: [{
    id: 'm1',
    name: 'Sara V.',
    avatar: 'https://i.pravatar.cc/150?img=47'
  }, {
    id: 'm2',
    name: 'Tudor L.',
    avatar: 'https://i.pravatar.cc/150?img=15'
  }, {
    id: 'm3',
    name: 'Bianca R.',
    avatar: 'https://i.pravatar.cc/150?img=44'
  }],
  comboAuthorId: 'm1'
};
const MOCK_ANUNT: AnuntData = ANDREI_ANUNT;
function resolveAnunt(email?: string): AnuntData {
  switch (email) {
    case 'olivia.p@izio.test':
      return OLIVIA_ANUNT;
    case 'sara.v@izio.test':
      return SARA_ANUNT;
    case 'andrei.p@izio.test':
      return ANDREI_ANUNT;
    default:
      return MOCK_ANUNT;
  }
}
function computeAnuntCompleteness(a: AnuntData) {
  const checks = [{
    ok: a.budgetMaxEur > 0,
    label: 'buget'
  }, {
    ok: !!a.neighborhoods,
    label: 'cartiere'
  }, {
    ok: !!a.searchBio && a.searchBio.length >= 30,
    label: 'descriere'
  }, {
    ok: a.amenities.length >= 3,
    label: 'facilități'
  }, {
    ok: !!a.moveInDate,
    label: 'data mutării'
  }];
  const passed = checks.filter(c => c.ok).length;
  const percent = Math.round(passed / checks.length * 100);
  const missing = checks.filter(c => !c.ok).map(c => c.label);
  return {
    percent,
    missing
  };
}
interface DashboardAnuntProps {
  navigate: (page: string, context?: any) => void;
}
export function DashboardAnunt({
  navigate
}: DashboardAnuntProps) {
  const session = getSession();
  const sessionKey = session?.email || 'guest';
  // L8.2 — if the user previously dismissed the succes celebration, the
  // active slot starts empty so they can create a new anunț.
  const initialDismissed = isSuccesDismissed(sessionKey);
  const [anunt, setAnunt] = useState<AnuntData | null>(initialDismissed ? null : resolveAnunt(session?.email));
  const [memberToRemove, setMemberToRemove] = useState<ComboMember | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [copied, setCopied] = useState(false);
  // L8 — finalization state
  const [finalization, setFinalization] = useState(() => getAnuntFinalization());
  const [movedInOpen, setMovedInOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // L8.1 — history + snapshot state
  const [history, setHistory] = useState<AnuntHistoryEntry[]>(() => getAnuntHistory());
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  // L8.2 — local dismiss flag so the FinalizedAnuntCard hides immediately.
  const [succesDismissed, setSuccesDismissed] = useState(initialDismissed);
  const isFinalized = !!finalization?.finalized && !succesDismissed;
  useEffect(() => {
    setFinalization(getAnuntFinalization());
    setHistory(getAnuntHistory());
  }, []);
  // L8.2 — dismiss the success banner: persist + clear the active slot so
  // EmptyAnuntSlot renders and the user can create a brand-new anunț.
  const handleDismissSucces = () => {
    dismissSucces(sessionKey);
    setSuccesDismissed(true);
    setAnunt(null);
  };
  const refreshHistory = () => setHistory(getAnuntHistory());
  const availableSavedOffers = (() => {
    if (!anunt) return [];
    return getOffersForTenant(anunt.slug).filter(o => o.status === 'saved').map(o => ({
      id: o.id,
      counterpartyName: o.fromName,
      counterpartyAvatar: o.fromAvatar,
      listingTitle: (o as any).listingPreview?.title,
      listingPrice: (o as any).listingPreview?.priceLabel
    }));
  })();
  const succesOffer = finalization?.succesOfferId && anunt ? getOffersForTenant(anunt.slug).find(o => o.id === finalization.succesOfferId) : undefined;
  // Build a frozen snapshot for the current anunț (used by markOfferAsSucces
  // and manual archive).
  const buildSnapshot = (): AnuntHistoryEntry['anuntSnapshot'] | null => {
    if (!anunt) return null;
    return {
      budgetMaxEur: anunt.budgetMaxEur,
      housingType: anunt.housingType,
      rooms: anunt.rooms,
      floor: anunt.floor,
      yearBuiltLabel: anunt.yearBuiltLabel,
      neighborhoods: anunt.neighborhoods,
      moveInDate: anunt.moveInDate,
      contractDuration: anunt.contractDuration,
      livingMode: anunt.livingMode,
      isSmoker: anunt.isSmoker,
      pets: anunt.pets,
      searchBio: anunt.searchBio,
      amenities: anunt.amenities
    };
  };
  const handleConfirmIzioMatch = (offerId: string) => {
    if (!anunt) return;
    if (offerId.startsWith('offer_')) {
      const snap = buildSnapshot();
      markOfferAsSucces(offerId, {
        slug: anunt.slug,
        neighborhoods: anunt.neighborhoods,
        budgetMaxEur: anunt.budgetMaxEur,
        moveInDate: anunt.moveInDate
      }, snap || undefined);
    }
    setFinalization(getAnuntFinalization());
    refreshHistory();
    setMovedInOpen(false);
    setAnunt(a => a ? {
      ...a,
      status: 'inactive' as ProfileStatus
    } : a);
  };
  const handleConfirmExternal = () => {
    finalizeAnuntExternally();
    setMovedInOpen(false);
    setAnunt(a => a ? {
      ...a,
      status: 'inactive' as ProfileStatus
    } : a);
  };
  const handlePause = () => {
    updateSession({
      profileStatus: 'inactive'
    });
    setMovedInOpen(false);
    setAnunt(a => a ? {
      ...a,
      status: 'inactive' as ProfileStatus
    } : a);
  };
  const handleRequestExternalReview = () => {
    setMovedInOpen(false);
    setReviewOpen(true);
  };
  const handleCancelFinalization = () => {
    cancelFinalization();
    setFinalization(null);
    refreshHistory();
    setAnunt(a => a ? {
      ...a,
      status: 'active' as ProfileStatus
    } : a);
  };
  // L8.1 — Manual archive: NO MovedInModal, NO offer archiving. Just shelve
  // the anunț into history and flip profileStatus.
  const handleManualArchive = () => {
    if (!anunt) return;
    const snap = buildSnapshot();
    if (!snap) return;
    archiveCurrentAnuntManually(anunt.slug, snap);
    refreshHistory();
    setConfirmArchive(false);
    // Wipe the local anunt so the empty-slot CTA renders. The history list
    // will contain the just-archived entry.
    setAnunt(null);
  };
  const handleRestoreFromHistory = (id: string) => {
    // For the prototype: deleting the history entry "restores" the anunț
    // back to the active slot. In a real backend we'd rehydrate the full
    // record; here we just resolve from the session demo data.
    const entry = history.find(h => h.id === id);
    if (!entry) return;
    removeAnuntHistory(id);
    refreshHistory();
    // If there's still no active anunț, hydrate one from the snapshot.
    if (!anunt) {
      setAnunt({
        id: `restored_${entry.id}`,
        slug: entry.slug,
        status: 'active',
        views: 0,
        offers: 0,
        budgetMaxEur: entry.anuntSnapshot.budgetMaxEur,
        housingType: entry.anuntSnapshot.housingType,
        rooms: entry.anuntSnapshot.rooms,
        floor: entry.anuntSnapshot.floor,
        yearBuiltLabel: entry.anuntSnapshot.yearBuiltLabel,
        neighborhoods: entry.anuntSnapshot.neighborhoods,
        moveInDate: entry.anuntSnapshot.moveInDate,
        contractDuration: entry.anuntSnapshot.contractDuration,
        livingMode: entry.anuntSnapshot.livingMode,
        isSmoker: entry.anuntSnapshot.isSmoker,
        pets: entry.anuntSnapshot.pets,
        searchBio: entry.anuntSnapshot.searchBio,
        amenities: entry.anuntSnapshot.amenities
      });
    }
    updateSession({
      profileStatus: 'active'
    });
  };
  const handleDuplicateFromHistory = (id: string) => {
    const entry = history.find(h => h.id === id);
    if (!entry) return;
    if (anunt) return; // active slot occupied
    setAnunt({
      id: `dup_${entry.id}_${Date.now()}`,
      slug: `${entry.slug}-copy`,
      status: 'active',
      views: 0,
      offers: 0,
      budgetMaxEur: entry.anuntSnapshot.budgetMaxEur,
      housingType: entry.anuntSnapshot.housingType,
      rooms: entry.anuntSnapshot.rooms,
      floor: entry.anuntSnapshot.floor,
      yearBuiltLabel: entry.anuntSnapshot.yearBuiltLabel,
      neighborhoods: entry.anuntSnapshot.neighborhoods,
      moveInDate: entry.anuntSnapshot.moveInDate,
      contractDuration: entry.anuntSnapshot.contractDuration,
      livingMode: entry.anuntSnapshot.livingMode,
      isSmoker: entry.anuntSnapshot.isSmoker,
      pets: entry.anuntSnapshot.pets,
      searchBio: entry.anuntSnapshot.searchBio,
      amenities: entry.anuntSnapshot.amenities
    });
    updateSession({
      profileStatus: 'active'
    });
  };
  const handleDeleteFromHistory = (id: string) => {
    removeAnuntHistory(id);
    refreshHistory();
  };
  const copyAnuntLink = async () => {
    if (!anunt) return;
    const url = typeof window !== 'undefined' ? `${window.location.origin}/chirias/${anunt.slug}` : `/chirias/${anunt.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  const viewAnuntAsPublic = () => {
    if (!anunt) return;
    navigate(PAGE_ROUTE.TENANT_ANUNT, {
      slug: anunt.slug,
      viewerIsOwner: true
    });
  };
  if (session?.role === 'supply') {
    return <div className="space-y-6" data-id="element-3240">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3241">Anunț</h1>
        <div className="rounded-2xl bg-dark-2 border border-dark-3 p-8 text-center" data-id="element-3242">
          <p className="text-sm text-light-3 max-w-md mx-auto" data-id="element-3243">
            Această secțiune e disponibilă doar pentru chiriași.
          </p>
        </div>
      </div>;
  }
  // L8.1 — Snapshot view (read-only overlay)
  if (activeSnapshotId) {
    const entry = history.find(h => h.id === activeSnapshotId);
    if (entry) {
      return <AnuntSnapshotView entry={entry} onBack={() => setActiveSnapshotId(null)} data-id="element-3244" />;
    }
  }
  const housingLabel = anunt ? HOUSING_TYPES.find(t => t.value === anunt.housingType)?.label || 'Apartament' : '';
  const locationPills = anunt ? [anunt.rooms, anunt.floor, anunt.yearBuiltLabel].filter(Boolean) as string[] : [];
  const {
    percent,
    missing
  } = anunt ? computeAnuntCompleteness(anunt) : {
    percent: 0,
    missing: [] as string[]
  };
  const composition = anunt ? [anunt.livingMode, anunt.isSmoker ? 'Fumător' : 'Nefumători', anunt.pets ? anunt.pets : null].filter(Boolean).join(' · ') : '';
  const members = anunt?.comboMembers || [];
  const memberCount = members.length;
  const isGroup = memberCount >= 2;
  const authorId = anunt?.comboAuthorId || members[0]?.id;
  const handleRemoveMember = () => {
    if (!memberToRemove) return;
    setAnunt(curr => {
      if (!curr) return curr;
      return {
        ...curr,
        comboMembers: (curr.comboMembers || []).filter(m => m.id !== memberToRemove.id)
      };
    });
    setMemberToRemove(null);
  };
  return <div className="space-y-8" data-id="element-3245">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3" data-id="element-3246">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3247">Anunț</h1>
        {anunt && <ActionMenu triggerAriaLabel="Acțiuni anunț" items={isFinalized ? [
      // L8.2 — reduced menu on finalized state: only allow
      // starting a fresh anunț.
      {
        label: 'Creează anunț nou',
        icon: <Plus size={18} weight="bold" data-id="element-3249" />,
        onClick: () => {
          handleDismissSucces();
          navigate(PAGE_ROUTE.ONBOARDING);
        }
      }] : [{
        label: 'Te-ai mutat?',
        icon: <HouseLine size={18} weight="regular" data-id="element-3250" />,
        onClick: () => setMovedInOpen(true)
      }, {
        label: 'Editează anunț',
        icon: <PencilSimple size={18} weight="regular" data-id="element-3251" />,
        onClick: () => {
          /* edit flow placeholder */
        }
      }, {
        label: copied ? 'Link copiat' : 'Copiază link',
        icon: copied ? <Check size={18} weight="bold" data-id="element-3252" /> : <LinkIcon size={18} weight="regular" data-id="element-3253" />,
        onClick: copyAnuntLink,
        closeOnClick: false,
        rightSlot: copied ? <Check size={14} weight="bold" className="text-lime-1" data-id="element-3254" /> : undefined
      }, {
        label: 'Vezi cum apari',
        icon: <Eye size={18} weight="regular" data-id="element-3255" />,
        onClick: viewAnuntAsPublic
      }, {
        label: 'Arhivează anunț',
        icon: <Archive size={18} weight="regular" data-id="element-3256" />,
        onClick: () => setConfirmArchive(true),
        destructive: true
      }]} data-id="element-3248" />}
      </div>

      {/* ─── ACTIVE SLOT ─────────────────────────────────────────────── */}
      <section className="space-y-6" data-id="element-3257">
        {!anunt ? <EmptyAnuntSlot hasHistory={history.length > 0} onCreateNew={() => navigate(PAGE_ROUTE.ONBOARDING)} onDuplicateLast={history.length > 0 ? () => handleDuplicateFromHistory(history[0].id) : undefined} data-id="element-3258" /> : isFinalized ? <FinalizedAnuntCard succesCounterpartyName={succesOffer?.fromName} succesCounterpartySubRole={succesOffer?.fromSubRole} succesCounterpartyAgencyName={succesOffer?.fromAgencyName} succesAt={finalization?.succesAt} succesListingTitle={(succesOffer as any)?.listingPreview?.title} onCancel={handleCancelFinalization} onViewArchive={() => navigate(PAGE_ROUTE.DASHBOARD_APLICARI)} onDismiss={handleDismissSucces} onClickCounterparty={(() => {
        const slug = supplySlugFromEmail(succesOffer?.fromEmail);
        return slug ? () => navigate(PAGE_ROUTE.LANDLORD_PROFILE, {
          slug
        }) : undefined;
      })()} data-id="element-3259" /> : <>
            <ProfileStatusBar status={anunt.status} views={anunt.views} offers={anunt.offers} onRequestPause={() => setMovedInOpen(true)} onStatusChange={next => setAnunt(a => a ? {
          ...a,
          status: next
        } : a)} data-id="element-3260" />

            {percent < 100 && <ProfileCompletenessNudge percent={percent} missingFields={missing} primaryLabel="Completează anunțul" data-id="element-3261" />}

            {/* CARD: Detalii anunț */}
            <article className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-3262">
              <header className="px-6 py-4 border-b border-dark-3" data-id="element-3263">
                <h2 className="text-base font-semibold text-light-1" data-id="element-3264">
                  Detalii anunț
                </h2>
              </header>

              <div className="px-6 py-7 space-y-7" data-id="element-3265">
                <ul className="space-y-3 text-sm text-light-2" data-id="element-3266">
                  <li className="flex items-center gap-3" data-id="element-3267">
                    <Wallet size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3268" />
                    <span data-id="element-3269">max {formatBudget(anunt.budgetMaxEur)}</span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-3270">
                    <MapPin size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3271" />
                    <span data-id="element-3272">{anunt.neighborhoods}</span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-3273">
                    <Flag size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3274" />
                    <span data-id="element-3275">
                      {anunt.moveInDate} · {anunt.contractDuration}
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-3276">
                    <Article size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3277" />
                    <span data-id="element-3278">{composition}</span>
                  </li>
                </ul>

                <div data-id="element-3279">
                  <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-3280">
                    Despre loc
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-3281">
                    <span className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal bg-dark-4 text-light-1 border border-dark-4" data-id="element-3282">
                      {housingLabel}
                    </span>
                    {locationPills.map(pill => <span key={pill} className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal border border-dark-4 text-light-2" data-id="element-3283">
                        {pill}
                      </span>)}
                  </div>
                </div>

                {anunt.amenities.length > 0 && <div data-id="element-3284">
                    <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-3285">
                      Facilități dorite
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-3286">
                      {anunt.amenities.map(a => <span key={a} className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal border border-dark-4 text-light-2" data-id="element-3287">
                          {a}
                        </span>)}
                    </div>
                  </div>}

                {anunt.searchBio && <div data-id="element-3288">
                    <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-3289">
                      Ce caut
                    </h3>
                    <p className="text-sm text-light-2 leading-relaxed" data-id="element-3290">
                      {anunt.searchBio}
                    </p>
                  </div>}
              </div>
            </article>

            {isGroup && <article className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-3291">
                <header className="flex items-center gap-3 px-6 py-4 border-b border-dark-3" data-id="element-3292">
                  <div className="w-9 h-9 rounded-lg bg-dark-3 flex items-center justify-center shrink-0" data-id="element-3293">
                    <Users size={16} weight="regular" className="text-light-2" data-id="element-3294" />
                  </div>
                  <div className="min-w-0" data-id="element-3295">
                    <h2 className="text-base font-semibold text-light-1" data-id="element-3296">
                      Membri grup
                    </h2>
                    <p className="text-xs text-light-3 mt-0.5" data-id="element-3297">
                      Toți pot vedea ofertele primite
                    </p>
                  </div>
                </header>

                <div className="px-6 py-6 space-y-3" data-id="element-3298">
                  {members.map(m => {
              const isAuthor = m.id === authorId;
              return <div key={m.id} className="flex items-center gap-4 p-3 rounded-xl bg-dark-1 border border-dark-3" data-id="element-3299">
                        {m.avatar ? <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-3300" /> : <div className="w-10 h-10 rounded-full bg-dark-3 flex items-center justify-center text-sm font-medium text-light-2 shrink-0" data-id="element-3301">
                            {m.name[0]}
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-3302">
                          <div className="flex items-center gap-2" data-id="element-3303">
                            <p className="text-sm font-medium text-light-1 truncate" data-id="element-3304">
                              {m.name}
                            </p>
                            {isAuthor && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-lime-5 text-lime-1 border border-lime-4 shrink-0" data-id="element-3305">
                                Admin
                              </span>}
                          </div>
                        </div>
                        {!isAuthor && <button onClick={() => setMemberToRemove(m)} className="inline-flex items-center gap-1 h-9 px-2 rounded-md text-xs font-medium text-light-3 hover:text-red-1 transition-colors cursor-pointer shrink-0" aria-label={`Elimină pe ${m.name}`} data-id="element-3306">
                            <Trash size={12} weight="bold" data-id="element-3307" />
                            Elimină
                          </button>}
                      </div>;
            })}
                </div>
              </article>}
          </>}
      </section>

      {/* ─── ISTORIC ANUNȚURI ─────────────────────────────────────────── */}
      {history.length > 0 && <section className="space-y-3" data-id="element-3308">
          <div className="flex items-center justify-between" data-id="element-3309">
            <h2 className="text-base font-semibold text-light-1" data-id="element-3310">
              Istoric anunțuri
            </h2>
            <span className="text-xs text-light-3" data-id="element-3311">{history.length}</span>
          </div>
          <p className="text-xs text-light-3 max-w-2xl" data-id="element-3312">
            Sesiunile tale închise. Apasă pe oricare pentru a vedea anunțul și
            ofertele din acel moment.
          </p>
          <div className="space-y-3" data-id="element-3313">
            {history.map(h => <AnuntHistoryCard key={h.id} entry={h} onOpen={id => setActiveSnapshotId(id)} onRestore={!anunt ? handleRestoreFromHistory : undefined} onDuplicate={!anunt ? handleDuplicateFromHistory : undefined} onDelete={handleDeleteFromHistory} data-id="element-3314" />)}
          </div>
        </section>}

      {/* Confirm member removal */}
      <ConfirmDialog open={!!memberToRemove} title="Elimini această persoană din grupul tău?" description={memberToRemove ? `${memberToRemove.name} nu va mai putea vedea ofertele primite pentru acest anunț. Dacă grupul rămâne cu o singură persoană, anunțul revine la o ofertă individuală.` : ''} confirmLabel="Da, elimină" cancelLabel="Anulează" variant="danger" onConfirm={handleRemoveMember} onCancel={() => setMemberToRemove(null)} dashboardOffset data-id="element-3315" />

      {/* L8.1 — Manual archive goes through ConfirmDialog ONLY. No MovedInModal. */}
      <ConfirmDialog open={confirmArchive} title="Arhivezi anunțul?" description="Anunțul nu va mai fi vizibil pentru proprietari și va apărea în Istoric anunțuri. Ofertele primite rămân neatinse. Îl poți restaura oricând." confirmLabel="Da, arhivează" cancelLabel="Anulează" variant="danger" onConfirm={handleManualArchive} onCancel={() => setConfirmArchive(false)} dashboardOffset data-id="element-3316" />

      {/* L8 — Te-ai mutat? (succes path only — manual archive does NOT open this) */}
      <MovedInModal isOpen={movedInOpen} onClose={() => setMovedInOpen(false)} mode="from-toggle" availableOffers={availableSavedOffers} onConfirmIzioMatch={handleConfirmIzioMatch} onConfirmExternal={handleConfirmExternal} onPause={handlePause} onRequestExternalReview={handleRequestExternalReview} data-id="element-3317" />

      <RequestReviewModal open={reviewOpen} onClose={() => setReviewOpen(false)} onSuccess={() => setReviewOpen(false)} navigate={navigate} mode="demand" data-id="element-3318" />
    </div>;
}